import { Dispatch, useReducer } from 'react';
import { getDefaultPageSize } from '../../components/queues/PagedDetailsList';

interface IState<TFilter = null> {
    page: number;
    pageSize: number;
    searchText: string;
    filter: TFilter;
    sortingColumn: string;
    isSortedDescending: boolean;
}

type Action<TFilter = null> =
    | { type: 'SetPageSize'; value: number; }
    | { type: 'UpdateSearchText'; value: string; }
    | { type: 'SetFilter', value: TFilter }
    | { type: 'PrevPage'; }
    | { type: 'NextPage'; }
    | { type: 'SetPage'; value: number }
    | { type: 'ApplySorting'; value: string };

function reducer<TFilter>(state: IState<TFilter>, action: Action<TFilter>): IState<TFilter> {
    const { page, searchText, filter, sortingColumn, isSortedDescending } = state;

    switch (action.type) {
        case 'SetPageSize':
            return { ...state, page: 0, pageSize: action.value };
        case 'UpdateSearchText':
            if (action.value !== searchText)
                return { ...state, page: 0, searchText: action.value };
            else
                return state;
        case 'SetFilter':
            if (action.value !== filter)
                return { ...state, page: 0, filter: action.value };
            else
                return state;
        case 'PrevPage':
            return { ...state, page: page - 1 };
        case 'NextPage':
            return { ...state, page: page + 1 };
        case 'SetPage':
            return { ...state, page: action.value };
        case 'ApplySorting':
            return { ...state, page: 0, sortingColumn: action.value, isSortedDescending: sortingColumn === action.value ? !isSortedDescending : false };
    }
}

function getBaseState(): IState {
    return {
        page: 0,
        pageSize: getDefaultPageSize(),
        searchText: '',
        filter: null,
        sortingColumn: '',
        isSortedDescending: false,
    };
}

type HookReturnType<TFilter> = [IState<TFilter>, Dispatch<Action<TFilter>>];

function usePagedListReducer<TFilter = null>(defaultState?: Partial<IState<TFilter>>): HookReturnType<TFilter> {
    return useReducer(reducer, { ...getBaseState(), ...defaultState }) as HookReturnType<TFilter>;
}

export default usePagedListReducer;